
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Bell, X, LoaderCircle } from "lucide-react";
import "./NotificationBell.css";
import { useNavigate } from "react-router-dom";

const API_URL = (
    process.env.REACT_APP_BACKEND_URL || ""
).replace(/\/+$/, "");

const NotificationBell = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [markingRead, setMarkingRead] = useState(null);
    const [error, setError] = useState("");

    const dropdownRef = useRef(null);

    const unreadCount = notifications.filter(
        (notification) => !notification.read
    ).length;

    // GET unread notifications
    const fetchNotifications = async () => {
        const token = localStorage.getItem("token");

        if (!token) {
            setError("Please log in to view notifications.");
            return;
        }

        if (!API_URL) {
            setError("Backend URL is not configured.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await axios.get(
                `${API_URL}/api/v1/notifications/unread`,
                {
                    headers: {
                        Accept: "*/*",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const data = response.data;

            const items = Array.isArray(data)
                ? data
                : Array.isArray(data.content)
                    ? data.content
                    : Array.isArray(data.data)
                        ? data.data
                        : [];

            setNotifications(items);
        } catch (err) {
            console.error("Error fetching notifications:", err);

            setError(
                err.response?.data?.message ||
                err.message ||
                "Unable to load notifications."
            );
        } finally {
            setLoading(false);
        }
    };

    // Open dropdown and fetch notifications
    const handleBellClick = () => {
        const nextOpen = !isOpen;
        setIsOpen(nextOpen);

        if (nextOpen) {
            fetchNotifications();
        }
    };
    const navigate = useNavigate();
    // PATCH mark notification as read
    const handleNotificationClick = async (notification) => {
        if (markingRead !== null) return;

        const token = localStorage.getItem("token");

        if (!token) {
            setError("Please log in again.");
            return;
        }

        setMarkingRead(notification.notificationId);
        setError("");

        try {
            if (!notification.read) {
                await axios.patch(
                    `${API_URL}/api/v1/notifications/${notification.notificationId}/read`,
                    {},
                    {
                        headers: {
                            Accept: "*/*",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                setNotifications((previous) =>
                    previous.filter(
                        (item) =>
                            item.notificationId !== notification.notificationId
                    )
                );
            }

            if (notification.targetUrl) {
                navigate(-1);
            }
        } catch (err) {
            console.error("Error updating notification:", err);

            setError(
                err.response?.data?.message ||
                err.message ||
                "Unable to update notification."
            );
        } finally {
            setMarkingRead(null);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        // Fetch notifications immediately when Navbar loads
        fetchNotifications();

        // Refresh notifications every 30 seconds
        const interval = setInterval(() => {
            fetchNotifications();
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    return (
        <div className="notification-wrapper" ref={dropdownRef}>
            <button
                type="button"
                className="notification-bell-button"
                onClick={handleBellClick}
                aria-label="Notifications"
                aria-expanded={isOpen}
            >
                <Bell size={23} />

                {unreadCount > 0 && (
                    <span className="notification-red-dot"></span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <div>
                            <h6>Notifications</h6>
                            <span>
                                {unreadCount} unread notification
                                {unreadCount !== 1 ? "s" : ""}
                            </span>
                        </div>

                        <button
                            type="button"
                            className="notification-close-button"
                            onClick={() => setIsOpen(false)}
                            aria-label="Close notifications"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {error && (
                        <div className="notification-error">{error}</div>
                    )}

                    {loading ? (
                        <div className="notification-state">
                            <LoaderCircle
                                className="notification-spinner"
                                size={22}
                            />
                            <span>Loading notifications...</span>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="notification-state">
                            <Bell size={28} />
                            <p>No unread notifications</p>
                        </div>
                    ) : (
                        <div className="notification-list">
                            {notifications.map((notification) => (
                                <button
                                    type="button"
                                    key={notification.notificationId}
                                    className="notification-item"
                                    disabled={markingRead !== null}
                                    onClick={() =>
                                        handleNotificationClick(notification)
                                    }
                                >
                                    <span className="notification-item-icon">
                                        <Bell size={18} />
                                    </span>

                                    <span className="notification-item-content">
                                        <strong>{notification.title}</strong>
                                        <span>{notification.message}</span>

                                        <small>
                                            {notification.createdAt
                                                ? new Date(
                                                    notification.createdAt
                                                ).toLocaleString("en-IN", {
                                                    day: "2-digit",
                                                    month: "short",
                                                    year: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                })
                                                : ""}
                                        </small>
                                    </span>

                                    {markingRead === notification.notificationId ? (
                                        <LoaderCircle
                                            className="notification-spinner"
                                            size={18}
                                        />
                                    ) : (
                                        <span className="notification-unread-dot" />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
